// API Service for Executive Mat Service
// Handles all backend API calls

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper for API calls
async function apiCall(endpoint, options = {}) {
  const url = `${API_URL}${endpoint}`;
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
    throw new Error(error.detail || error.message || 'API Error');
  }
  
  return response.json();
}

// ============== PUBLIC API ==============

export const api = {
  // Products
  async getProducts() {
    return apiCall('/api/products');
  },
  
  // Public Settings
  async getPublicSettings() {
    return apiCall('/api/settings/public');
  },
  
  // Reward Tiers
  async getRewardTiers() {
    return apiCall('/api/rewards/tiers');
  },
  
  // Inquiries
  async createInquiry(intent = 'start_service') {
    return apiCall('/api/inquiries', {
      method: 'POST',
      body: JSON.stringify({ intent }),
    });
  },
  
  async getInquiry(token) {
    return apiCall(`/api/inquiries/${token}`);
  },
  
  async updateInquiry(token, data) {
    return apiCall(`/api/inquiries/${token}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },
  
  async submitInquiry(token) {
    return apiCall(`/api/inquiries/${token}/submit`, {
      method: 'POST',
    });
  },
  
  // Quotes
  async createQuote(data) {
    return apiCall('/api/quotes', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  
  async getQuote(token) {
    return apiCall(`/api/quotes/${token}`);
  },
  
  async acceptQuote(token) {
    return apiCall(`/api/quotes/${token}/accept`, {
      method: 'POST',
    });
  },
};

// ============== ADMIN API ==============

export const adminApi = {
  // Auth
  async login(email, password) {
    return apiCall('/api/admin/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  },
  
  async logout(token) {
    return apiCall('/api/admin/auth/logout', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
  },
  
  async getCurrentUser(token) {
    return apiCall('/api/admin/auth/me', {
      headers: { Authorization: `Bearer ${token}` },
    });
  },
  
  // Inquiries
  async getInquiries(token, params = {}) {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/api/admin/inquiries${query ? `?${query}` : ''}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },
  
  async getInquiry(token, inquiryId) {
    return apiCall(`/api/admin/inquiries/${inquiryId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },
  
  async updateInquiry(token, inquiryId, data) {
    return apiCall(`/api/admin/inquiries/${inquiryId}`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify(data),
    });
  },
  
  // Quotes
  async getQuotes(token, params = {}) {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/api/admin/quotes${query ? `?${query}` : ''}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },
  
  async getQuote(token, quoteId) {
    return apiCall(`/api/admin/quotes/${quoteId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },
  
  async updateQuote(token, quoteId, data) {
    return apiCall(`/api/admin/quotes/${quoteId}`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify(data),
    });
  },
  
  async getQuoteResumeLink(token, quoteId) {
    return apiCall(`/api/admin/quotes/${quoteId}/resume-link`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },
  
  async sendQuoteEmail(token, quoteId) {
    return apiCall(`/api/admin/quotes/${quoteId}/send-email`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
  },
  
  // Products
  async getProducts(token) {
    return apiCall('/api/admin/products', {
      headers: { Authorization: `Bearer ${token}` },
    });
  },
  
  async createProduct(token, data) {
    return apiCall('/api/admin/products', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify(data),
    });
  },
  
  async updateProduct(token, productId, data) {
    return apiCall(`/api/admin/products/${productId}`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify(data),
    });
  },
  
  async deleteProduct(token, productId) {
    return apiCall(`/api/admin/products/${productId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
  },
  
  // Reward Tiers
  async getRewardTiers(token) {
    return apiCall('/api/admin/rewards/tiers', {
      headers: { Authorization: `Bearer ${token}` },
    });
  },
  
  async createRewardTier(token, data) {
    return apiCall('/api/admin/rewards/tiers', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify(data),
    });
  },
  
  async updateRewardTier(token, tierId, data) {
    return apiCall(`/api/admin/rewards/tiers/${tierId}`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify(data),
    });
  },
  
  // Settings
  async getSettings(token) {
    return apiCall('/api/admin/settings', {
      headers: { Authorization: `Bearer ${token}` },
    });
  },
  
  async updateSettings(token, data) {
    return apiCall('/api/admin/settings', {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify(data),
    });
  },
  
  // Users
  async getUsers(token) {
    return apiCall('/api/admin/users', {
      headers: { Authorization: `Bearer ${token}` },
    });
  },
  
  async createUser(token, data) {
    return apiCall('/api/admin/users', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify(data),
    });
  },
  
  async updateUser(token, userId, data) {
    return apiCall(`/api/admin/users/${userId}`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify(data),
    });
  },
  
  async resetPassword(token, userId, newPassword) {
    return apiCall(`/api/admin/users/${userId}/reset-password?new_password=${encodeURIComponent(newPassword)}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
  },
  
  // Audit Logs
  async getAuditLogs(token, params = {}) {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/api/admin/audit-logs${query ? `?${query}` : ''}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },
  
  // Reports
  async getFunnelReport(token, params = {}) {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/api/admin/reports/funnel${query ? `?${query}` : ''}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },
  
  async getDashboardSummary(token) {
    return apiCall('/api/admin/reports/summary', {
      headers: { Authorization: `Bearer ${token}` },
    });
  },
};

export default api;
